import { useEffect } from 'react';

import type { LearnerProfile_ProfileSlug } from '__generated__/graphql-types-do-not-use';

import config from 'js/app/config';
import { useNavigate } from 'js/lib/useRouter';

import type { LearnerProfile } from 'bundles/account-profile/common/types';
import { useLearnerProfileProfileSlugQuery } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useLearnerProfileQuery';

import _t from 'i18n!nls/account-profile';

type ProfileWithSlugs = {
  /* eslint-disable camelcase */
  slugs?: LearnerProfile_ProfileSlug[] | null;
};

export const PROFILE_BY_EXTERNALID_PREFIX = 'user';
export const PROFILE_BY_SLUG_PREFIX = 'learner';
export const URL_PROFILE_EXTERNALID_PATH = config.url.base + PROFILE_BY_EXTERNALID_PREFIX;
export const URL_PROFILE_SLUG_PATH = config.url.base + PROFILE_BY_SLUG_PREFIX;

export const SLUG_MAX_COUNT = 10;
export const SLUG_MAX_LENGTH = 100;
export const SLUG_MIN_LENGTH = 3;

export const VALIDATION_MESSAGES = {
  USER_UNAUTHORIZED: 'user_unauthorized',
  SLUG_INVALID_FORMAT: 'slug_invalid_format',
  SLUG_ALREADY_EXISTS: 'slug_already_exists',
  TOO_MANY_SLUGS: 'too_many_slugs',
  UNKNOWN_ERROR: 'unknown_error',
} as const;

export function getErrorMessage(key: string): string {
  /* eslint-disable camelcase */
  const messages: Record<string, string> = {
    [VALIDATION_MESSAGES.USER_UNAUTHORIZED]: _t('You must be logged into continue.'),
    [VALIDATION_MESSAGES.SLUG_INVALID_FORMAT]: _t(
      'Link may contain only letters, numbers, or dashes, and must be at least three characters long.'
    ),
    [VALIDATION_MESSAGES.SLUG_ALREADY_EXISTS]: _t('That link is already taken, please try another.'),
    [VALIDATION_MESSAGES.TOO_MANY_SLUGS]: _t('Limit reached. Maximum of 10 URLs per learner.'),
    [VALIDATION_MESSAGES.UNKNOWN_ERROR]: _t('An unknown error has occurred, please try again'),
  };
  return messages[key] || getErrorMessage(VALIDATION_MESSAGES.UNKNOWN_ERROR);
}

export function getCurrentSlug(profile: ProfileWithSlugs): string | undefined {
  const slugs = [...(profile.slugs || [])];

  if (slugs.length < 1) {
    return undefined;
  }

  const slugRecord = slugs.reduce((prev, current) => {
    return current.createdAt > prev.createdAt ? current : prev;
  });

  return slugRecord?.slug || undefined;
}

export function getProfileUrl(profile: Partial<LearnerProfile>, currentSlug?: string): string {
  if (currentSlug) {
    return `${URL_PROFILE_SLUG_PATH}/${currentSlug}`;
  }

  if (profile?.slugs?.length) {
    return `${URL_PROFILE_SLUG_PATH}/${getCurrentSlug(profile)}`;
  }

  return `${URL_PROFILE_EXTERNALID_PATH}/${profile.externalId}`;
}

export function getValidationLabel(errors: string[]): string {
  return errors.map((e) => getErrorMessage(e)).join('\n');
}

export function isValidSlug(slug?: string | null): boolean {
  // Regex explanation:
  // - ^(?!\-): Positive lookahead to make sure it doesn't start with a '-'
  // - (?!.*[-]{2}): Positive lookahead to make sure there are no two consecutive dashes
  // - [a-z0-9-]+: Match lower-case letters, numerals, and dashes
  // - (?<!-)$: Negative lookbehind to ensure the last character is not a dash
  const regex = /^(?!-)(?!.*-{2})[a-z0-9-]+[^-_]$/;

  // Check if the slug contains only dashes
  const onlyDashes = /^-+$/;

  // Check if the slug satisfies all the specified conditions
  return (
    !!slug &&
    slug.length >= SLUG_MIN_LENGTH &&
    slug.length <= SLUG_MAX_LENGTH &&
    !onlyDashes.test(slug) && // Ensure it doesn't contain only dashes
    regex.test(slug)
  );
}

export function generateDefaultSlug(profile: Partial<LearnerProfile>): string {
  if (!profile.fullName) {
    return profile.externalId || '';
  }

  const generatedSlug = profile.fullName
    .toLowerCase()
    // convert spaces to dashes
    .replace(/ /g, '-')
    // remove all other invalid chars
    .replace(/[^a-z0-9-]/g, '')
    // append a random 4-digit number to reduce the chances of collision
    .concat(`-${Math.floor(Math.random() * (10000 - 1000) + 1000)}`);

  // only use the generated slug if it valdiates
  return isValidSlug(generatedSlug) ? generatedSlug : profile.externalId || '';
}

/**
 * Fetches profile by slug. If redirect param supplied, also check the results to see if
 * the slug used to query is the most recent. If not, reirect to the profile page with latest.
 */
export function useProfileBySlugWithRedirect(profileSlug: string, redirect?: boolean) {
  const { learnerProfile, isProfileLoading, refetchLearnerProfile, error } =
    useLearnerProfileProfileSlugQuery(profileSlug);
  const navigate = useNavigate();

  useEffect(() => {
    if (redirect && learnerProfile?.slugs) {
      const latestSlug = getCurrentSlug(learnerProfile);

      if (latestSlug && latestSlug !== profileSlug) {
        navigate(`/${PROFILE_BY_SLUG_PREFIX}/${latestSlug}`);
      }
    }
  }, [navigate, learnerProfile, profileSlug, redirect]);

  return {
    learnerProfile,
    isProfileLoading,
    refetchLearnerProfile,
    error,
  };
}
